import { PositionType } from 'data/types';
import {
  OpenNeighborRunsType,
  OpenNeighborSpaceType
} from 'utils/open-neighbor-opening-runs-types';
import { getSpaceEdgeRunsFromFeet } from 'utils/opening-layout';
import { SpaceEdgeRunGeometryType } from 'utils/opening-layout-types';
import { runOutwardNormal } from 'utils/opening-position';
import { EdgeRunType } from 'utils/opening-position-types';
import {
  segmentsAreCollinear,
  unclampedOffsetOnSegment,
  wallSegmentFromEndpoints
} from 'utils/wall-segment';
import { WallSegmentType } from 'utils/wall-segment-types';

const CONTIGUOUS_RUN_EPSILON_FT = 0.01;

export function geometryOriginFeet(
  geometryFeet: SpaceEdgeRunGeometryType
): PositionType {
  return { x: geometryFeet.position.x, y: geometryFeet.position.y };
}

function nearlyEqualFt(a: number, b: number): boolean {
  return Math.abs(a - b) < CONTIGUOUS_RUN_EPSILON_FT;
}

function runSegmentAbsolute(
  run: EdgeRunType,
  originFeet: PositionType
): WallSegmentType {
  return wallSegmentFromEndpoints(
    { x: originFeet.x + run.start.x, y: originFeet.y + run.start.y },
    { x: originFeet.x + run.end.x, y: originFeet.y + run.end.y },
    {
      cardinal: run.edge,
      outwardNormal: runOutwardNormal(run),
      edgeIndex: run.edgeIndex
    }
  );
}

function facesSameWay(a: WallSegmentType, b: WallSegmentType): boolean {
  return (
    a.outwardNormal.x * b.outwardNormal.x +
      a.outwardNormal.y * b.outwardNormal.y >
    0
  );
}

function collinearNeighborSegments(
  segment: WallSegmentType,
  neighbors: OpenNeighborRunsType[]
): WallSegmentType[] {
  return neighbors.flatMap((neighbor) =>
    neighbor.runs
      .map((neighborRun) =>
        runSegmentAbsolute(neighborRun, neighbor.originFeet)
      )
      .filter(
        (neighborSegment) =>
          segmentsAreCollinear(segment, neighborSegment) &&
          facesSameWay(segment, neighborSegment)
      )
  );
}

function neighborInterval(
  segment: WallSegmentType,
  neighborSegment: WallSegmentType
): { from: number; to: number } {
  const t1 = unclampedOffsetOnSegment(segment, neighborSegment.start);
  const t2 = unclampedOffsetOnSegment(segment, neighborSegment.end);
  return { from: Math.min(t1, t2), to: Math.max(t1, t2) };
}

function longestSegmentLength(segments: WallSegmentType[]): number {
  return Math.max(0, ...segments.map((segment) => segment.lengthFt));
}

export function openingRunWithOpenNeighborOverhang(
  run: EdgeRunType,
  originFeet: PositionType,
  neighbors: OpenNeighborRunsType[]
): EdgeRunType {
  const segment = runSegmentAbsolute(run, originFeet);
  const collinear = collinearNeighborSegments(segment, neighbors);
  const overhangStartFt = longestSegmentLength(
    collinear.filter((neighborSegment) =>
      nearlyEqualFt(neighborInterval(segment, neighborSegment).to, 0)
    )
  );
  const overhangEndFt = longestSegmentLength(
    collinear.filter((neighborSegment) =>
      nearlyEqualFt(
        neighborInterval(segment, neighborSegment).from,
        segment.lengthFt
      )
    )
  );

  if (overhangStartFt === 0 && overhangEndFt === 0) {
    return run;
  }

  return { ...run, overhangStartFt, overhangEndFt };
}

export function openingRunsWithOpenNeighborOverhang(
  runs: EdgeRunType[],
  originFeet: PositionType,
  neighbors: OpenNeighborRunsType[]
): EdgeRunType[] {
  return runs.map((run) =>
    openingRunWithOpenNeighborOverhang(run, originFeet, neighbors)
  );
}

function neighborWallRuns(neighbor: OpenNeighborSpaceType): EdgeRunType[] {
  return getSpaceEdgeRunsFromFeet(
    neighbor.geometryFeet,
    neighbor.footprints
  ).filter((run) => neighbor.wallEdges.includes(run.edge));
}

export function openNeighborOpeningRuns(
  neighbors: OpenNeighborSpaceType[]
): OpenNeighborRunsType[] {
  return neighbors.map((neighbor) => ({
    originFeet: geometryOriginFeet(neighbor.geometryFeet),
    runs: neighborWallRuns(neighbor)
  }));
}
